<template>
	<div class="dashboard_reports">
		<div class="main_div">
			<div class="tab_content">
				<!-- Sin <keep-alive> a propósito: cada tab se destruye al cambiar
					y refetchea en mounted. Cachearlos sería contraproducente —
					sus watchers de `fetchSignature` quedarían activos aun
					inactivos, así que tocar un filtro global dispararía el fetch
					de TODOS los tabs cacheados. Para que keep-alive valga la pena
					habría que volver lazy el fetch de cada tab primero.
					El :key es OBLIGATORIO: Activos/Pasivos/Patrimonio comparten
					el mismo componente (BalanceLevelTab), y sin key Vue patchea
					la instancia in-place al cambiar entre ellos — mounted() (el
					único trigger del fetch inicial) no vuelve a correr y el tab
					nuevo queda con la tabla vacía hasta tocar un filtro. -->
				<component :is="activeTab.component" :key="activeNavTab" v-bind="activeTab.props" />
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from "vuex";
import ComingSoon from "@/components/generics/ComingSoon.vue";
import ResultadosTab from "@/views/financialReports/tabs/ResultadosTab.vue";
import VentasTab from "@/views/financialReports/tabs/VentasTab.vue";
import CostosTab from "@/views/financialReports/tabs/CostosTab.vue";
import GastosTab from "@/views/financialReports/tabs/GastosTab.vue";
import RentabilidadTab from "@/views/financialReports/tabs/RentabilidadTab.vue";
import BalanceLevelTab from "@/views/financialReports/tabs/BalanceLevelTab.vue";
import BalanceLevelDetailTab from "@/views/financialReports/tabs/BalanceLevelDetailTab.vue";
import BalanceResumenTab from "@/views/financialReports/tabs/BalanceResumenTab.vue";
import BalanceKpisTab from "@/views/financialReports/tabs/BalanceKpisTab.vue";
import NoOperTab from "@/views/financialReports/tabs/NoOperTab.vue";
import KpisTab from "@/views/financialReports/tabs/KpisTab.vue";
import { TAB_IDS } from "@/views/financialReports/config/tabs";
import { ASSET_COUNTERPART_TYPES, LIABILITY_COUNTERPART_TYPES } from "@/config/reportStructure";

// Registry tab id → { component, props }. Reemplaza la cadena de v-if/v-else-if
// con un look-up declarativo: agregar un tab nuevo requiere una sola entry y no
// toca el template. `component` es el objeto SFC importado (no un string), así
// `<component :is>` lo resuelve sin registrarlo en `components` — una sola
// fuente, sin acople string↔registro. `props` por-entry (con `v-bind` en el
// template) cubre el label de ComingSoon en Proyecciones.
const TAB_REGISTRY = Object.freeze({
	[TAB_IDS.DASHBOARD]:    { component: ComingSoon,      props: { label: "Dashboard" } },
	[TAB_IDS.RESULTADOS]:   { component: ResultadosTab,   props: {} },
	[TAB_IDS.VENTAS]:       { component: VentasTab,       props: {} },
	[TAB_IDS.COSTOS]:       { component: CostosTab,       props: {} },
	[TAB_IDS.GASTOS]:       { component: GastosTab,       props: {} },
	[TAB_IDS.RENTABILIDAD]: { component: RentabilidadTab, props: {} },
	[TAB_IDS.ACTIVOS]:      { component: BalanceLevelTab, props: {
		namespace: "assetsReport",
		title: "Activos",
		description: "Analiza la composición y evolución del saldo de tus activos",
		theme: "activos",
		counterpartTypes: ASSET_COUNTERPART_TYPES,
	} },
	[TAB_IDS.NO_OPER]:      { component: NoOperTab,       props: {} },
	[TAB_IDS.KPIS]:         { component: KpisTab,         props: {} },
	// Tabs sin vista propia todavía → placeholder ComingSoon (mismo patrón
	// que Dashboard). El sidebar ya los lista (Balance / Proyecciones).
	[TAB_IDS.BALANCE_RESUMEN]: { component: BalanceResumenTab, props: {} },
	[TAB_IDS.PASIVOS]:         { component: BalanceLevelTab, props: {
		namespace: "liabilitiesReport",
		title: "Pasivos",
		description: "Analiza la composición y evolución del saldo de tus pasivos",
		theme: "activos",
		counterpartTypes: LIABILITY_COUNTERPART_TYPES,
	} },
	[TAB_IDS.PATRIMONIO]:      { component: BalanceLevelTab, props: {
		namespace: "equityReport",
		title: "Patrimonio",
		description: "Analiza la composición y evolución del saldo de tu patrimonio",
		theme: "activos",
		counterpartTypes: [],
	} },
	[TAB_IDS.BALANCE_KPIS]:    { component: BalanceKpisTab, props: {} },
	[TAB_IDS.PRESUPUESTO]:     { component: ComingSoon, props: { label: "Presupuesto" } },
	[TAB_IDS.PROYECCION_CAJA]: { component: ComingSoon, props: { label: "Proyección de Caja" } },
});

// Variante de los tabs de balance por nivel para empresas de MAYOR (ERP,
// has_ledger): la misma tabla del Resumen de Balance acotada a su nivel 1,
// con todo el nivel 3 desplegado (BalanceLevelDetailTab). Las empresas de
// clasificador/SII siguen con BalanceLevelTab (entries del TAB_REGISTRY).
const ERP_BALANCE_LEVEL_TABS = Object.freeze({
	[TAB_IDS.ACTIVOS]: { component: BalanceLevelDetailTab, props: {
		level1: "Activos",
		title: "Activos",
		description: "Analiza la composición y evolución del saldo de tus activos",
	} },
	[TAB_IDS.PASIVOS]: { component: BalanceLevelDetailTab, props: {
		level1: "Pasivos",
		title: "Pasivos",
		description: "Analiza la composición y evolución del saldo de tus pasivos",
	} },
	[TAB_IDS.PATRIMONIO]: { component: BalanceLevelDetailTab, props: {
		level1: "Patrimonio",
		title: "Patrimonio",
		description: "Analiza la composición y evolución del saldo de tu patrimonio",
	} },
});

// Tab default por sección cuando la URL (aún) no trae un ?tab válido. Cubre el
// hueco de la navegación del sidebar: Dashboard.vue setea visibleSection en
// sync pero el $router.replace que escribe ?tab es async — sin esto, el primer
// render de "Estado de Resultados"/"Balance" caía al fallback Dashboard
// (ComingSoon) por unos frames antes de mostrar el Resumen. Mismos defaults
// que declara el sidebar (navConfig: defaultTab por grupo).
const SECTION_DEFAULT_TAB = Object.freeze({
	estado_resultados: TAB_IDS.RESULTADOS,
	balance: TAB_IDS.BALANCE_RESUMEN,
	proyecciones: TAB_IDS.PRESUPUESTO,
});

export default {
	name: "DashboardReports",
	props: {
		// Sección activa según Dashboard.vue (reports / estado_resultados /
		// balance / proyecciones). Sólo alimenta el default de tab mientras la
		// URL no trae un ?tab válido — la URL sigue siendo la fuente de verdad.
		section: { type: String, default: "" },
	},
	computed: {
		...mapState("userAuth", ["company"]),

		// El tab activo se deriva del query param — la URL es la fuente única
		// de verdad. Antes vivía en data() + watcher inmediato; la computed
		// elimina state mutable redundante y es forward-compatible con
		// Composition API (mapea 1:1 a un `computed(() => ...)` en setup).
		// La navegación (escribir $route.query.tab) vive en el Sidebar y en
		// Dashboard.vue:onSectionChange — este view es read-only sobre la URL.
		// Sin ?tab válido (o mientras el replace async todavía no lo escribió),
		// cae al default de la sección activa en vez de al Dashboard.
		activeNavTab() {
			const tab = this.$route.query.tab;
			if (TAB_REGISTRY[tab]) return tab;
			return SECTION_DEFAULT_TAB[this.section] || TAB_IDS.DASHBOARD;
		},
		activeTab() {
			const entry = TAB_REGISTRY[this.activeNavTab];
			const hasLedger = Boolean(this.company && this.company.has_ledger);
			// Empresa de mayor (ERP): Activos/Pasivos/Patrimonio muestran la
			// tabla del Resumen de Balance acotada a su nivel 1, con el nivel 3
			// desplegado.
			if (hasLedger && ERP_BALANCE_LEVEL_TABS[this.activeNavTab]) {
				return ERP_BALANCE_LEVEL_TABS[this.activeNavTab];
			}
			// Sin data de ERP (mismo criterio que el sidebar en MainLayout) los
			// "activos" del balance representan la inversión en activos, no el
			// saldo contable del mayor.
			if (this.activeNavTab === TAB_IDS.ACTIVOS && !hasLedger) {
				return {
					component: entry.component,
					props: {
						...entry.props,
						title: "Inversión en activos",
						description: "Analiza la evolución de la inversión en tus activos",
					},
				};
			}
			return entry;
		},
	},
};
</script>

<style lang="scss" scoped>
.main_div {
	// El outer main.dashboard reserva var(--app-sidebar-w) para el rail;
	// acá sólo damos respiración de contenido al primer carácter.
	padding-left: clamp(16px, 2vw, 32px);
	padding-right: clamp(10px, 5.5vw, 93px);
	padding-bottom: 30px;
}

.tab_content {
	min-height: 400px;
}
</style>
